/**
 * Plafond du forfait décidé par le serveur (DEV-621 écran M3, DEV-2254).
 *
 * Le serveur décide, l'écran affiche. Ce module pur :
 * - lit le bloc `plafond` servi par l'API (réponse utilisateur, puis
 *   `GET /company/billing/source/count` après chaque ajout réussi) ;
 * - en déduit l'état affiché (sous le forfait, sursis, blocage) ;
 * - rédige les textes de l'écran à partir des seuls nombres servis ;
 * - lit le refus 409 `forfait_atteint` renvoyé à l'ajout d'une source.
 *
 * Règles de rédaction : « facturées », « nous », ton formel, dates en
 * français, aucun détail technique, aucun nom de marque.
 */
import { ref } from "vue"
import type { CompanyPlafond, CompositionPlafond } from "@core/types/auth"

// ================================
// TYPES
// ================================

/** État du plafond affiché par les formulaires d'ajout */
export type EtatPlafond = "absent" | "sous_forfait" | "sursis" | "blocage"

/** Message prêt à afficher dans un formulaire d'ajout */
export type MessagePlafond = {
    /** `warn` : avertissement non bloquant (sursis) ; `error` : ajout bloqué */
    severite: "warn" | "error"
    titre: string
    texte: string
}

/**
 * Refus servi à l'ajout (HTTP 409, `code: forfait_atteint`).
 * `forfait` et `facturees` sont `null` quand le corps est absent ou illisible :
 * l'écran affiche alors un texte générique, jamais le code.
 */
export type RefusForfait = {
    forfait: number | null
    facturees: number | null
    periodeFin: string | null
    prochainAjoutLe: string | null
    composition: CompositionPlafond | null
    /** Sous-feuille libérables servie dans le 409 ; `null` si non mesurée */
    placesLiberables: number | null
    /** Offre servie dans le 409 ; `null` si absente (rédaction EA) */
    offre?: OffrePlafond | null
}

/** Offre dont la règle s'applique (EM : la mise à jour ne compte jamais) */
export type OffrePlafond = "EA" | "EM"

/** Lit l'offre servie ; `null` si absente ou inconnue */
const lireOffre = (x: unknown): OffrePlafond | null =>
    x === "EA" || x === "EM" ? x : null

/** Code du refus servi par l'API à l'ajout d'une source au-delà du forfait */
export const CODE_FORFAIT_ATTEINT = "forfait_atteint"

/** Les cinq feuilles de l'arbre du décompte servi */
const FEUILLES_COMPOSITION = [
    "sur_le_compte_ajoutees_dans_la_periode",
    "sur_le_compte_ajoutees_avant",
    "supprimees_ajoutees_dans_la_periode",
    "supprimees_apres_mise_a_jour",
    "supprimees_liberees",
] as const

// ================================
// LECTURE DU BLOC SERVI
// ================================

const estNombre = (x: unknown): x is number =>
    typeof x === "number" && Number.isFinite(x)
const dateOuNull = (x: unknown): string | null =>
    typeof x === "string" && x !== ""
        ? x
        : x instanceof Date
          ? x.toISOString()
          : null

/**
 * Lit l'arbre du décompte (cinq feuilles). `null` si une feuille manque :
 * la phrase de composition est alors omise.
 */
export function lireComposition(brut: unknown): CompositionPlafond | null {
    if (!brut || typeof brut !== "object") return null
    const c = brut as Record<string, unknown>
    if (!FEUILLES_COMPOSITION.every((f) => estNombre(c[f]))) return null
    return Object.fromEntries(
        FEUILLES_COMPOSITION.map((f) => [f, c[f] as number])
    ) as CompositionPlafond
}

/**
 * Lit le bloc `plafond` servi. Retourne `null` s'il est absent ou mal formé :
 * l'écran garde alors son calcul historique (le front peut partir avant l'API).
 * @param brut - Valeur brute de `operation.plafond` ou du champ `plafond`
 */
export function lirePlafondServi(brut: unknown): CompanyPlafond | null {
    if (!brut || typeof brut !== "object") return null
    const p = brut as Record<string, unknown>
    if (!estNombre(p.forfait) || !estNombre(p.facturees)) return null
    if (typeof p.blocage !== "boolean") return null
    return {
        forfait: p.forfait,
        facturees: p.facturees,
        periode_fin: dateOuNull(p.periode_fin),
        blocage: p.blocage,
        blocage_a_partir_du: dateOuNull(p.blocage_a_partir_du),
        places_disponibles: estNombre(p.places_disponibles)
            ? p.places_disponibles
            : null,
        composition: lireComposition(p.composition),
        places_liberables: estNombre(p.places_liberables)
            ? p.places_liberables
            : null,
        offre: lireOffre(p.offre),
    }
}

/**
 * État affiché :
 * - `blocage` : le serveur refuse l'ajout ;
 * - `sursis` : pas de blocage, un blocage est annoncé et le compte est au forfait ou au-delà ;
 * - `sous_forfait` : rien à afficher ;
 * - `absent` : bloc non servi (repli sur le calcul historique).
 */
export function etatPlafond(plafond: CompanyPlafond | null): EtatPlafond {
    if (!plafond) return "absent"
    if (plafond.blocage) return "blocage"
    if (
        plafond.blocage_a_partir_du !== null &&
        plafond.facturees >= plafond.forfait
    ) {
        return "sursis"
    }
    return "sous_forfait"
}

// ================================
// DATES
// ================================

const PARIS = "Europe/Paris"

function lireDate(iso: string | null | undefined): Date | null {
    if (!iso) return null
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Date ISO en toutes lettres, sans l'année (« 1er novembre »), au fuseau de Paris.
 * Retourne `null` pour une date absente ou illisible.
 */
export function dateEnLettres(iso: string | null | undefined): string | null {
    const d = lireDate(iso)
    if (!d) return null
    const texte = new Intl.DateTimeFormat("fr-FR", {
        day: "numeric",
        month: "long",
        timeZone: PARIS,
    }).format(d)
    return texte.replace(/^1 /, "1er ")
}

/**
 * Date ISO au format JJ/MM (« 31/10 »), au fuseau de Paris, telle que servie.
 * Retourne `null` pour une date absente ou illisible.
 */
export function dateJourMois(iso: string | null | undefined): string | null {
    const d = lireDate(iso)
    if (!d) return null
    return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        timeZone: PARIS,
    }).format(d)
}

// ================================
// TEXTES
// ================================

/** « 1 source » / « 12 sources » */
const sources = (n: number): string => `${n} source${n > 1 ? "s" : ""}`
/** « 1 source facturée » / « 12 sources facturées » */
const sourcesFacturees = (n: number): string =>
    n > 1 ? `${n} sources facturées` : `${n} source facturée`
/** « 1 supprimée » / « 3 supprimées » */
const supprimees = (n: number): string => `${n} supprimée${n > 1 ? "s" : ""}`

/**
 * Sources dont la suppression libère la place tout de suite : en Energy
 * Management, la mise à jour ne compte jamais (« ajoutées avant la
 * période ») ; sinon, et sans offre servie, la rédaction Energy Analytics.
 */
export function sourcesLiberables(offre?: OffrePlafond | null): string {
    return offre === "EM"
        ? "des sources ajoutées avant la période"
        : "des sources qui n'ont été ni ajoutées ni mises à jour pendant la période"
}

/** Titre commun de l'état bloqué (maquette M3) */
export const TITRE_FORFAIT_ATTEINT = "Forfait atteint sur la période en cours"
/** Titre de l'avertissement de sursis */
export const TITRE_SURSIS = "Votre compte dépasse son forfait"
/** Texte du refus quand le serveur n'en a pas servi les nombres */
export const TEXTE_REFUS_GENERIQUE =
    "Votre forfait de sources est atteint. Libérez des places ou adaptez votre forfait."

/**
 * Texte du sursis : avertissement non bloquant, l'ajout reste possible.
 */
export function texteSursis(plafond: CompanyPlafond): string {
    const date = dateEnLettres(plafond.blocage_a_partir_du)
    const debut = `Vous avez ${sourcesFacturees(plafond.facturees)} pour un forfait de ${plafond.forfait} sur la période en cours.`
    const qui = sourcesLiberables(plafond.offre)
    const suite = date
        ? ` À partir du ${date}, l'ajout de sources sera bloqué tant que votre compte dépasse son forfait : d'ici là, libérez des places en supprimant ${qui}, ou adaptez votre forfait.`
        : ` L'ajout de sources sera bientôt bloqué tant que votre compte dépasse son forfait : libérez des places en supprimant ${qui}, ou adaptez votre forfait.`
    return debut + suite
}

/**
 * Phrase de composition (maquette M3) : « X sur votre compte, Y supprimées
 * pendant la période après un ajout, Z supprimées après une mise à jour ». Les parts
 * supprimées à zéro sont omises. `null` sans composition servie.
 */
export function phraseComposition(
    composition: CompositionPlafond | null,
    offre?: OffrePlafond | null
): string | null {
    if (!composition) return null
    const surLeCompte =
        composition.sur_le_compte_ajoutees_dans_la_periode +
        composition.sur_le_compte_ajoutees_avant
    const parts = [`${surLeCompte} sur votre compte`]
    // Energy Management : la mise à jour ne compte jamais, les supprimées
    // encore facturées se disent ensemble, sans « après une mise à jour »
    if (offre === "EM") {
        const n =
            composition.supprimees_ajoutees_dans_la_periode +
            composition.supprimees_apres_mise_a_jour
        if (n > 0) parts.push(`${supprimees(n)} pendant la période`)
        return parts.join(", ")
    }
    if (composition.supprimees_ajoutees_dans_la_periode > 0) {
        parts.push(
            `${supprimees(composition.supprimees_ajoutees_dans_la_periode)} pendant la période après un ajout`
        )
    }
    if (composition.supprimees_apres_mise_a_jour > 0) {
        parts.push(
            `${supprimees(composition.supprimees_apres_mise_a_jour)} après une mise à jour`
        )
    }
    return parts.join(", ")
}

/**
 * Texte de l'état bloqué (maquette M3), avec les seuls nombres servis.
 * La phrase de composition n'apparaît que si `composition` est servie.
 */
export function texteBlocage(entree: {
    forfait: number
    facturees: number
    periodeFin?: string | null
    prochainAjoutLe?: string | null
    composition?: CompositionPlafond | null
    placesLiberables?: number | null
    offre?: OffrePlafond | null
}): string {
    const fin = dateJourMois(entree.periodeFin)
    const periode = fin
        ? `Sur la période en cours, jusqu'au ${fin}, `
        : "Sur la période en cours, "
    const nombre =
        entree.facturees > 1
            ? `${entree.facturees} sources sont facturées`
            : `${entree.facturees} source est facturée`
    const compo = phraseComposition(entree.composition ?? null, entree.offre)
    let texte = `Votre forfait couvre ${sources(entree.forfait)} par période. ${periode}${nombre}${compo ? ` : ${compo}` : ""}.`
    const libre = dateEnLettres(entree.prochainAjoutLe)
    texte += libre
        ? ` Vous pourrez ajouter des sources à partir du ${libre}, ou augmenter votre forfait.`
        : " Pour ajouter des sources, augmentez votre forfait."
    const liberables = entree.placesLiberables ?? 0
    if (liberables > 0) {
        texte += ` Supprimez ${sourcesLiberables(entree.offre)} pour libérer des places tout de suite (${liberables} possible${liberables > 1 ? "s" : ""}).`
    }
    return texte
}

/**
 * Texte affiché quand les sources saisies dans le formulaire dépassent les places servies.
 */
export function texteDepassementApresAjout(plafond: CompanyPlafond): string {
    const fin = dateJourMois(plafond.periode_fin)
    const places = plafond.places_disponibles ?? 0
    const periode = fin
        ? `sur la période en cours, jusqu'au ${fin}`
        : "sur la période en cours"
    const reste =
        places > 1
            ? `Vous pouvez encore ajouter ${places} sources ${periode}.`
            : places === 1
              ? `Vous pouvez encore ajouter 1 source ${periode}.`
              : `Vous ne pouvez plus ajouter de source ${periode}.`
    return `${reste} Retirez des sources de ce formulaire, ou augmentez votre forfait.`
}

/**
 * Message à afficher pour un plafond servi, ou `null` sous le forfait / bloc absent.
 */
export function messagePlafond(
    plafond: CompanyPlafond | null
): MessagePlafond | null {
    const etat = etatPlafond(plafond)
    if (!plafond || etat === "absent" || etat === "sous_forfait") return null
    if (etat === "sursis") {
        return {
            severite: "warn",
            titre: TITRE_SURSIS,
            texte: texteSursis(plafond),
        }
    }
    return {
        severite: "error",
        titre: TITRE_FORFAIT_ATTEINT,
        texte: texteBlocage({
            forfait: plafond.forfait,
            facturees: plafond.facturees,
            periodeFin: plafond.periode_fin,
            composition: plafond.composition,
            placesLiberables: plafond.places_liberables,
            offre: plafond.offre ?? null,
        }),
    }
}

/**
 * Message de l'état bloqué rédigé à partir d'un refus 409 ; texte générique
 * quand le refus ne porte pas ses nombres.
 */
export function messageRefusForfait(refus: RefusForfait): MessagePlafond {
    const texte =
        refus.forfait === null || refus.facturees === null
            ? TEXTE_REFUS_GENERIQUE
            : texteBlocage({
                  forfait: refus.forfait,
                  facturees: refus.facturees,
                  periodeFin: refus.periodeFin,
                  prochainAjoutLe: refus.prochainAjoutLe,
                  composition: refus.composition,
                  placesLiberables: refus.placesLiberables,
                  offre: refus.offre ?? null,
              })
    return { severite: "error", titre: TITRE_FORFAIT_ATTEINT, texte }
}

// ================================
// REFUS 409 À L'AJOUT
// ================================

/**
 * Lit le refus `forfait_atteint` d'une erreur de service. Le corps de l'API est
 * `{ detail: { code: "forfait_atteint", context: { forfait, facturees,
 * periode_fin, comptees, periode: { debut, fin }, composition,
 * prochain_ajout_possible_le, places_liberables } } }` (ou à plat), normalisé
 * par `handleServiceCall` en `{ status, code, context }`.
 * Un corps absent ou illisible donne un refus sans nombres (texte générique).
 * @returns Le refus, ou `null` si l'erreur n'en est pas un
 */
export function lireRefusForfait(error: unknown): RefusForfait | null {
    if (!error || typeof error !== "object") return null
    const e = error as { status?: unknown; code?: unknown; context?: unknown }
    if (e.code !== CODE_FORFAIT_ATTEINT) return null
    if (e.status != null && e.status !== 409) return null
    const c = (
        e.context && typeof e.context === "object" ? e.context : {}
    ) as Record<string, unknown>
    const periode = (
        c.periode && typeof c.periode === "object" ? c.periode : {}
    ) as Record<string, unknown>
    const facturees = estNombre(c.facturees)
        ? c.facturees
        : estNombre(c.comptees)
          ? c.comptees
          : null
    const forfait = estNombre(c.forfait) ? c.forfait : null
    const lisible = forfait !== null && facturees !== null
    return {
        forfait: lisible ? forfait : null,
        facturees: lisible ? facturees : null,
        periodeFin: dateOuNull(c.periode_fin) ?? dateOuNull(periode.fin),
        prochainAjoutLe: dateOuNull(c.prochain_ajout_possible_le),
        composition: lireComposition(c.composition),
        placesLiberables: estNombre(c.places_liberables)
            ? c.places_liberables
            : null,
        offre: lireOffre(c.offre),
    }
}

/**
 * Dernier refus `forfait_atteint` reçu à l'ajout, partagé entre les formulaires.
 * Renseigné par `handleServiceCall`, effacé à l'ouverture d'un formulaire d'ajout.
 */
export const dernierRefusForfait = ref<RefusForfait | null>(null)

/**
 * Publie le refus porté par l'erreur, s'il y en a un.
 * @returns Le refus lu, ou `null`
 */
export function signalerRefusForfait(error: unknown): RefusForfait | null {
    const refus = lireRefusForfait(error)
    if (refus) dernierRefusForfait.value = refus
    return refus
}
